import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  PlatformUserTOTPSetup200,
  usePlatformUserTOTPDelete,
  usePlatformUserTOTPSetup,
  usePlatformUserTOTPVerify,
} from '@cloudquery/api-platform';
import { useBaseAuth } from '@cloudquery/base-users';
import { useForm } from 'react-hook-form';

interface MfaContextType {
  handleResetMfa: () => Promise<void>;
  handleVerifyMfa: () => Promise<any>;
  mfaSetupData: PlatformUserTOTPSetup200 | null;
  needsToSetupMfa?: boolean;
  needsToVerifyMfa?: boolean;
  setVerifyMfaCode: (code: string) => void;
  userMfaConfigured?: boolean;
  verifyMfaCode: string;
}

const MfaContext = createContext<MfaContextType | null>(null);

interface MfaProviderProps {
  children: ReactNode;
}

export function MfaProvider({ children }: MfaProviderProps) {
  const { handleSubmit } = useForm();
  const { refresh, userMfaConfigured, userMfaRequired } = useBaseAuth();

  const [mfaSetupData, setMfaSetupData] = useState<PlatformUserTOTPSetup200 | null>(null);
  const [verifyMfaCode, setVerifyMfaCode] = useState('');

  const { mutateAsync: setupMfa } = usePlatformUserTOTPSetup();
  const { mutateAsync: verifyMfa } = usePlatformUserTOTPVerify();
  const { mutateAsync: resetMfa } = usePlatformUserTOTPDelete();

  const needsToSetupMfa = userMfaRequired && !userMfaConfigured;
  const needsToVerifyMfa = userMfaRequired && userMfaConfigured;

  const loadMfaSetup = useCallback(async () => {
    try {
      const data = await setupMfa();
      setMfaSetupData(data.data);
    } catch {
      setMfaSetupData(null);
    }
  }, [setupMfa]);

  useEffect(() => {
    if (!userMfaConfigured) {
      loadMfaSetup();
    }
  }, [loadMfaSetup, userMfaConfigured]);

  const handleVerifyMfa = useCallback(async () => {
    await verifyMfa({
      data: {
        otp: verifyMfaCode,
      },
    });

    await refresh();
    setVerifyMfaCode('');
  }, [verifyMfaCode, verifyMfa, refresh]);

  const handleResetMfa = useCallback(async () => {
    await resetMfa();
    await refresh();
  }, [resetMfa, refresh]);

  const contextValue = useMemo<MfaContextType>(
    () => ({
      handleResetMfa,
      handleVerifyMfa: handleSubmit(handleVerifyMfa),
      mfaSetupData,
      needsToSetupMfa,
      needsToVerifyMfa,
      setVerifyMfaCode,
      userMfaConfigured,
      verifyMfaCode,
    }),
    [
      mfaSetupData,
      verifyMfaCode,
      needsToSetupMfa,
      needsToVerifyMfa,
      handleVerifyMfa,
      handleSubmit,
      handleResetMfa,
      userMfaConfigured,
    ],
  );

  return <MfaContext.Provider value={contextValue}>{children}</MfaContext.Provider>;
}

export function useMfaContext(): MfaContextType {
  const context = useContext(MfaContext);

  if (!context) {
    throw new Error('useMfaContext must be used within a MfaProvider');
  }

  return context;
}
